import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Wrench,
  Truck,
  ArrowRight,
  ArrowLeft,
  Play,
  CheckCircle,
  CheckCircle2,
  Clock,
  DollarSign,
  BarChart3,
  Shield,
  Zap,
  Users,
  Package,
  FileText,
  MapPin,
  Phone,
  Mail,
  Calendar,
  AlertTriangle,
  Target,
  Award,
  Sparkles,
  X,
  MessageCircle,
  Send,
  Bell,
  Smartphone,
  Camera,
  Droplets,
  FlaskConical,
  Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { ShareButtons } from "@/components/ShareButtons";

// Digital Isaac Assets
const DIGITAL_ISAAC = "https://static.prod-images.emergentagent.com/jobs/0bc119bf-ae15-43e9-9f13-14b53e074c94/images/fe24d5e280d09d6a7d0c6d06f6b149605eaa9a0fa1be0b67e18ee25456bf0b6f.png";
const DIGITAL_ISAAC_VIDEO = "https://customer-assets.emergentagent.com/job_hospital-mgmt-27/artifacts/r8ou0x9f_freepik_animate-image-as-a-digital-concierge-for-hospital-_kling_1080p_16-9_24fps_97399.mp4";

// Demo slide data
const DEMO_SLIDES = [
  // Slide 1: Welcome
  {
    id: "welcome",
    type: "welcome",
    title: "Welcome to AskIsaac",
    subtitle: "On-Demand Healthcare Operations",
    description: "One platform. Supplies. Maintenance. On Demand. Push-button access to your backyard network — mobilized in minutes, not weeks.",
    features: [
      "On-demand supply network",
      "On-demand service network",
      "Live GPS tracking & verification",
      "Asset lifecycle management"
    ],
    stats: [
      { label: "STAT Response", value: "2 hrs" },
      { label: "Priority Response", value: "4-8 hrs" },
      { label: "Network Size", value: "Growing" }
    ]
  },
  // Slide 2: Our Services - The "One Roof" Value Proposition
  {
    id: "services",
    type: "features",
    title: "Everything Under One Roof",
    subtitle: "From First Request to Last — It's Done. Call Us Again When You Need Us.",
    categories: [
      {
        name: "Healthcare Water Purification",
        icon: Droplets,
        features: [
          "Installation & project management",
          "Maintenance & repairs",
          "AAMI ST108 water sampling",
          "RO systems, DI water, softeners"
        ]
      },
      {
        name: "Licensed Contractors On-Demand",
        icon: Wrench,
        features: [
          "Plumbing — healthcare experienced",
          "Electrical — licensed & insured",
          "HVAC — critical environment certified",
          "All pre-vetted for healthcare"
        ]
      },
      {
        name: "General Contractor Services",
        icon: Shield,
        features: [
          "Containment & infection control",
          "Healthcare facility renovations",
          "Compliance-first construction",
          "Project management"
        ]
      },
      {
        name: "Water Sampling & Testing",
        icon: FlaskConical,
        features: [
          "AAMI ST108 compliant sampling",
          "Lab testing coordination",
          "Results interpretation",
          "Compliance documentation"
        ]
      }
    ]
  },
  // Slide 3: For Hospitals
  {
    id: "hospitals",
    type: "persona",
    persona: "hospital",
    title: "For Healthcare Facilities",
    subtitle: "On-Demand Access to Supplies & Services",
    icon: Building2,
    color: "blue",
    problems: [
      { issue: "GPO says 'backordered' — no compliant path forward", icon: AlertTriangle },
      { issue: "Equipment down — weeks to schedule repair", icon: Clock },
      { issue: "No visibility — did contractor actually show up?", icon: Users },
      { issue: "Manual 3-quote process causes delays", icon: Package }
    ],
    solutions: [
      { feature: "Supply Network", description: "Anonymous bidding for backorders, GPO failures, emergency inventory — first responder wins" },
      { feature: "Service Network", description: "Same-day contractor dispatch with live GPS check-in verification" },
      { feature: "Response Tiers", description: "Standard (24-48 hrs), Priority (4-8 hrs), or STAT (2 hrs) based on urgency" },
      { feature: "Asset Registry", description: "QR-coded equipment with full service history at your fingertips" }
    ],
    testimonial: null
  },
  // Slide 3: For Contractors
  {
    id: "contractors",
    type: "persona",
    persona: "contractor",
    title: "For Service Contractors",
    subtitle: "Join the On-Demand Service Network",
    icon: Wrench,
    color: "emerald",
    problems: [
      { issue: "Chasing invoices and late payments", icon: DollarSign },
      { issue: "Missed dispatch notifications", icon: Phone },
      { issue: "No proof of work documentation", icon: FileText },
      { issue: "No competitive advantage for fast response", icon: Clock }
    ],
    solutions: [
      { feature: "Anonymous Bidding", description: "Compete fairly — first responder with compliant bid wins the job" },
      { feature: "GPS Check-In/Check-Out", description: "Verified on-site arrival triggers real-time notifications to hospital & admin" },
      { feature: "Premium Tiers", description: "STAT and Priority jobs pay higher margins for faster response" },
      { feature: "Streamlined Billing", description: "Auto-generated invoices based on tracked time and work" }
    ],
    testimonial: {
      quote: "The anonymous bidding system rewards contractors who are ready to work. No favoritism, no gatekeepers.",
      author: "Sterile Mate",
      role: "Service Manager"
    }
  },
  // Slide 4: For Suppliers
  {
    id: "suppliers",
    type: "persona",
    persona: "supplier",
    title: "For Medical Suppliers",
    subtitle: "Join the On-Demand Supply Network",
    icon: Truck,
    color: "amber",
    problems: [
      { issue: "Difficulty reaching hospital buyers directly", icon: Target },
      { issue: "Long quote-to-order cycles", icon: Clock },
      { issue: "No visibility into competition", icon: Package },
      { issue: "Missed emergency sourcing opportunities", icon: DollarSign }
    ],
    solutions: [
      { feature: "Anonymous Bidding", description: "Bid without seeing competitors — first responder with inventory wins" },
      { feature: "Emergency RFQ Alerts", description: "Get notified instantly when hospitals need emergency inventory" },
      { feature: "Premium Margins", description: "STAT tier orders command premium pricing for urgent fulfillment" },
      { feature: "Direct Hospital Access", description: "Connect with verified healthcare procurement teams" }
    ],
    testimonial: {
      quote: "AskIsaac opened doors to healthcare accounts we couldn't reach before. The emergency sourcing channel is invaluable.",
      author: "Empire Bio Diagnostics",
      role: "Sales Director"
    }
  },
  // Slide 5: Asset Lifecycle Management
  {
    id: "assets",
    type: "features",
    title: "Asset Lifecycle Management",
    subtitle: "QR-Coded Equipment Registry with Complete History",
    categories: [
      {
        name: "Equipment Registry",
        icon: Package,
        features: [
          "QR code on every asset",
          "Instant lookup via mobile scan",
          "Complete service history",
          "Warranty tracking"
        ]
      },
      {
        name: "Preventive Maintenance",
        icon: Calendar,
        features: [
          "Automated PM schedules",
          "Never miss critical servicing",
          "AAMI ST108 compliance",
          "Equipment-specific protocols"
        ]
      },
      {
        name: "Service Documentation",
        icon: FileText,
        features: [
          "Photos & work notes",
          "Parts used tracking",
          "Contractor performance",
          "Audit-ready records"
        ]
      },
      {
        name: "Real-Time Visibility",
        icon: BarChart3,
        features: [
          "Equipment uptime metrics",
          "Cost per asset tracking",
          "Maintenance backlogs",
          "Warranty expiration alerts"
        ]
      }
    ]
  },
  // Slide 6: Telegram Integration & Web Chat
  {
    id: "telegram",
    type: "telegram",
    title: "Isaac Bot — Everywhere You Work",
    subtitle: "Two-Way Workflow via Telegram @askisaac_bot + Web Chat",
    botUsername: "@askisaac_bot",
    features: [
      {
        icon: Bell,
        title: "Instant Alerts",
        description: "Get notified when jobs are assigned, quotes are needed, or contractors arrive on-site"
      },
      {
        icon: MessageCircle,
        title: "Send Updates",
        description: "Contractors: /arrived, /update, /complete - directly update job status from the field"
      },
      {
        icon: Send,
        title: "Submit Quotes",
        description: "Suppliers: Receive RFQs, submit quotes for admin processing with markup"
      },
      {
        icon: Smartphone,
        title: "Works Everywhere",
        description: "Telegram bot + Web chat on desktop — same features, your choice of interface"
      },
      {
        icon: Play,
        title: "Demo Simulations",
        description: "/demo command runs full lifecycle demos (Maintenance & Backorder) for sales presentations"
      }
    ],
    userBenefits: [
      { role: "Hospitals", benefit: "Real-time GPS alerts when contractors check in on-site" },
      { role: "Contractors", benefit: "/arrived triggers live notification to hospital & admin" },
      { role: "Suppliers", benefit: "Instant quote requests for STAT, Priority, or Standard orders" },
      { role: "Desktop Users", benefit: "Ask Isaac web chat with full demo simulations & AI assistance" }
    ]
  },
  // Slide 7: Multi-Stakeholder Sync & Supplier Chain of Command
  {
    id: "collaboration",
    type: "features",
    title: "Collaboration & Team Management",
    subtitle: "Coordinate Every Stakeholder on Every Ticket",
    categories: [
      {
        name: "Multi-Stakeholder Sync",
        icon: Users,
        features: [
          "Tag plumbers, nurses, suppliers on any ticket",
          "Email invites for unregistered collaborators",
          "Real-time notifications for all tagged users",
          "Viral growth loop — tagged users join platform"
        ]
      },
      {
        name: "Supplier Chain of Command",
        icon: Shield,
        features: [
          "4-tier role hierarchy (Admin, Supervisor, Tech, Buyer)",
          "Internal job assignment and routing",
          "Supervisor approval for completed work",
          "Authorized buyer controls for quotes/backorders"
        ]
      },
      {
        name: "Hospital Department Silos",
        icon: Building2,
        features: [
          "Department-based dashboard filtering",
          "Facilities Engineering vs Supply Chain views",
          "Role-specific onboarding flows",
          "Custom access controls per department"
        ]
      },
      {
        name: "Unified Communication",
        icon: MessageCircle,
        features: [
          "All ticket updates in one thread",
          "Email + Telegram + WebSocket notifications",
          "Complete audit trail for compliance",
          "HIPAA-ready message logging"
        ]
      }
    ]
  },
  // Slide 8: Hospital-Grade Security Suite
  {
    id: "hipaa",
    type: "features",
    title: "Hospital-Grade Security Suite",
    subtitle: "Zero-Knowledge Architecture + HIPAA-Ready Compliance",
    categories: [
      {
        name: "Zero-Knowledge Architecture",
        icon: Shield,
        features: [
          "Passwords never visible to admins (bcrypt hashed)",
          "Session timeout (30-min inactivity auto-logout)",
          "Credential masking in all API responses",
          "No plain-text secrets in logs or UI"
        ]
      },
      {
        name: "Multi-Factor Authentication",
        icon: Lock,
        features: [
          "TOTP-based MFA (Google Authenticator, Authy)",
          "Backup codes for emergency access",
          "Per-user MFA enforcement options",
          "QR code setup in account settings"
        ]
      },
      {
        name: "WORM Audit Logs",
        icon: FileText,
        features: [
          "Write-Once-Read-Many immutable logging",
          "Every action timestamped & attributed",
          "7-year retention (HIPAA compliant)",
          "One-click export for Joint Commission"
        ]
      },
      {
        name: "PHI Detection & Redaction",
        icon: CheckCircle2,
        features: [
          "Auto-detect SSN, DOB, MRN patterns",
          "Real-time warnings before data exposure",
          "Credit card pattern scrubbing",
          "Configurable sensitivity levels"
        ]
      }
    ]
  },
  // Slide 9: Response Tiers & ROI
  {
    id: "roi",
    type: "roi",
    title: "Response Guarantee Tiers",
    subtitle: "Choose the Speed Your Situation Demands",
    metrics: [
      {
        metric: "24-48 hrs",
        label: "Standard Response",
        description: "Routine maintenance & inventory requests"
      },
      {
        metric: "4-8 hrs",
        label: "Priority Response",
        description: "Urgent needs requiring same-day attention"
      },
      {
        metric: "2 hrs",
        label: "STAT Response",
        description: "OR/ER emergencies — surgery-critical"
      },
      {
        metric: "Live",
        label: "GPS Verification",
        description: "Real-time contractor arrival confirmation"
      }
    ],
    caseStudy: null
  },
  // Slide 9: Partnership Tiers & Strategic Moats
  {
    id: "tiers-moats",
    type: "tiers",
    title: "Partnership Tiers & Strategic Moats",
    subtitle: "Flexible engagement models backed by defensible competitive advantages",
    tiers: [
      {
        tier: "Tier 1",
        name: "Managed Compliance",
        mode: "Managed Mode",
        description: "Full-service project and compliance management. We handle everything — from contractor dispatch to final compliance documentation.",
        color: "cyan"
      },
      {
        tier: "Tier 2",
        name: "Enterprise Marketplace",
        mode: "Platform Mode",
        description: "Self-service access to our vetted network with competitive bidding. You run the projects, we provide the platform and network.",
        color: "emerald",
        popular: true
      },
      {
        tier: "Tier 3",
        name: "White-Label Infrastructure",
        mode: "SaaS Moat",
        description: "License the AskIsaac platform under your own brand. Full infrastructure with your branding, your network, your rules.",
        color: "violet"
      }
    ],
    moats: [
      {
        category: "Institutional Access Moat",
        subtitle: "The \"Hard\" Moat",
        color: "cyan",
        items: [
          { title: "6 Years of Texas Medical Center (TMC) Trust", description: "Direct, \"boots-on-the-ground\" relationships with facility leadership in the world's largest medical complex." },
          { title: "SDVOSB Federal Gateway", description: "Strategic partnerships that provide a \"Fast-Track\" for nationwide VA facility access, bypassing standard private-sector competition." },
          { title: "Tier 1 Public Health System Partnership", description: "Proven integration and active project management within one of the largest public health systems in the U.S." }
        ]
      },
      {
        category: "Technical & Supply Moat",
        subtitle: "The \"Capability\" Moat",
        color: "emerald",
        items: [
          { title: "Healthcare Water Purification Experts", description: "AskIsaac delivers specialized AAMI ST108 water purification solutions for Sterile Processing and Dialysis." },
          { title: "Enterprise Integration", description: "Advanced discussions with Techinquest for deep software-level integration to automate compliance reporting." },
          { title: "Managed Compliance IP", description: "Proprietary logic for the fee per transaction Managed Mode and fee per sale of Supply Margin on critical infrastructure projects." }
        ]
      }
    ]
  },
  // Slide 10: Get Started
  {
    id: "cta",
    type: "cta",
    title: "Ready to Mobilize Your Network?",
    subtitle: "Post a need. Get bids in minutes. Track fulfillment live.",
    options: [
      {
        type: "Hospital",
        action: "Request a Demo",
        link: "/request-demo?type=hospital",
        description: "On-demand access to supplies & services",
        icon: Building2
      },
      {
        type: "Contractor",
        action: "Join Service Network",
        link: "/request-demo?type=contractor",
        description: "Win jobs through anonymous bidding",
        icon: Wrench
      },
      {
        type: "Supplier",
        action: "Join Supply Network",
        link: "/request-demo?type=supplier",
        description: "Reach hospitals when GPO fails",
        icon: Truck
      }
    ],
    contact: {
      email: "info@sterilemate.com",
      tollFree: "800.988.3892",
      direct: "281-456-3458",
      website: "sterilemate.com"
    }
  }
];

export default function DemoPresentation({ open, onClose }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slide = DEMO_SLIDES[currentSlide];

  const nextSlide = () => {
    if (currentSlide < DEMO_SLIDES.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const renderSlide = () => {
    switch (slide.type) {
      case "welcome":
        return (
          <div className="space-y-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 rounded-full mb-4">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-200 text-sm font-medium">Healthcare Innovation Platform</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{slide.title}</h2>
              <p className="text-xl text-white font-medium mb-2">{slide.subtitle}</p>
              <p className="text-slate-300 max-w-2xl mx-auto">{slide.description}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {slide.features.map((feature, idx) => (
                <div key={idx} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mb-2" />
                  <p className="text-white text-sm font-medium">{feature}</p>
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-8">
              {slide.stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-slate-300 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case "persona":
        const colorClasses = {
          blue: { bg: "bg-blue-500/20", text: "text-blue-300", border: "border-blue-500/30" },
          emerald: { bg: "bg-emerald-500/20", text: "text-emerald-300", border: "border-emerald-500/30" },
          amber: { bg: "bg-amber-500/20", text: "text-amber-300", border: "border-amber-500/30" }
        };
        const colors = colorClasses[slide.color] || colorClasses.blue;
        const Icon = slide.icon;

        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <div className={`p-3 rounded-xl ${colors.bg}`}>
                <Icon className={`w-8 h-8 ${colors.text}`} />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white">{slide.title}</h2>
                <p className={`${colors.text} font-medium`}>{slide.subtitle}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Problems */}
              <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/20">
                <h3 className="text-lg font-semibold text-red-300 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  The Challenge
                </h3>
                <div className="space-y-3">
                  {slide.problems.map((problem, idx) => {
                    const ProblemIcon = problem.icon;
                    return (
                      <div key={idx} className="flex items-start gap-3">
                        <ProblemIcon className="w-4 h-4 text-red-300 mt-1 flex-shrink-0" />
                        <p className="text-slate-200 text-sm">{problem.issue}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Solutions */}
              <div className={`${colors.bg} rounded-xl p-5 border ${colors.border}`}>
                <h3 className={`text-lg font-semibold ${colors.text} mb-4 flex items-center gap-2`}>
                  <Zap className="w-5 h-5" />
                  Our Solution
                </h3>
                <div className="space-y-3">
                  {slide.solutions.map((solution, idx) => (
                    <div key={idx} className="space-y-1">
                      <p className="text-white font-medium text-sm">{solution.feature}</p>
                      <p className="text-slate-300 text-xs">{solution.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Testimonial */}
            {slide.testimonial && (
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <div className="flex items-start gap-4">
                  <div className="text-4xl text-slate-500">&ldquo;</div>
                  <div>
                    <p className="text-slate-200 italic mb-3">{slide.testimonial.quote}</p>
                    <div>
                      <p className="text-white font-medium">{slide.testimonial.author}</p>
                      <p className="text-slate-400 text-sm">{slide.testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "features":
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {slide.categories.map((category, idx) => {
                const CategoryIcon = category.icon;
                return (
                  <div key={idx} className="bg-slate-800/50 rounded-xl p-5 border border-slate-700 hover:border-cyan-500/30 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-cyan-500/20 rounded-lg">
                        <CategoryIcon className="w-5 h-5 text-cyan-300" />
                      </div>
                      <h3 className="text-lg font-semibold text-white">{category.name}</h3>
                    </div>
                    <ul className="space-y-2">
                      {category.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-center gap-2 text-slate-200 text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "telegram":
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-3 px-4 py-2 bg-blue-500/20 rounded-full mb-4">
                <Send className="w-5 h-5 text-blue-400" />
                <span className="text-blue-200 text-sm font-medium">{slide.botUsername}</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              {slide.features.map((feature, idx) => {
                const FeatureIcon = feature.icon;
                return (
                  <div key={idx} className="bg-blue-500/10 rounded-xl p-5 border border-blue-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <FeatureIcon className="w-5 h-5 text-blue-300" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">{feature.title}</h3>
                        <p className="text-slate-300 text-sm">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* User Benefits */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                Benefits by Role
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {slide.userBenefits.map((item, idx) => (
                  <div key={idx} className="text-center p-4 bg-slate-800 rounded-lg">
                    <p className="text-cyan-300 font-medium mb-2">{item.role}</p>
                    <p className="text-slate-300 text-sm">{item.benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <a 
                href="https://t.me/askisaac_bot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
              >
                <Send className="w-5 h-5" />
                Open @askisaac_bot in Telegram
              </a>
            </div>
          </div>
        );

      case "roi":
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {slide.metrics.map((item, idx) => (
                <div key={idx} className="bg-gradient-to-b from-cyan-500/20 to-transparent rounded-xl p-5 border border-cyan-500/20 text-center">
                  <p className="text-4xl font-bold text-cyan-300 mb-2">{item.metric}</p>
                  <p className="text-white font-medium text-sm mb-1">{item.label}</p>
                  <p className="text-slate-400 text-xs">{item.description}</p>
                </div>
              ))}
            </div>

            {/* Case Study */}
            {slide.caseStudy && (
              <div className="bg-emerald-500/10 rounded-xl p-6 border border-emerald-500/20">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5 text-emerald-300" />
                  <h3 className="text-lg font-semibold text-emerald-300">Case Study: {slide.caseStudy.client}</h3>
                </div>
                <div className="grid md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-slate-400 text-sm">Before</p>
                    <p className="text-white font-semibold">{slide.caseStudy.before}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">After 12 Months</p>
                    <p className="text-white font-semibold">{slide.caseStudy.after}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Annual Savings</p>
                    <p className="text-emerald-300 font-bold text-xl">{slide.caseStudy.savings}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "tiers":
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            {/* Partnership Tiers */}
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {slide.tiers.map((tier, idx) => {
                const tierColors = {
                  cyan: { bg: "bg-cyan-500/20", border: "border-cyan-500/30", text: "text-cyan-300", badge: "bg-cyan-500" },
                  emerald: { bg: "bg-emerald-500/20", border: "border-emerald-500/30", text: "text-emerald-300", badge: "bg-emerald-500" },
                  violet: { bg: "bg-violet-500/20", border: "border-violet-500/30", text: "text-violet-300", badge: "bg-violet-500" }
                };
                const colors = tierColors[tier.color] || tierColors.cyan;
                return (
                  <div 
                    key={idx} 
                    className={`relative rounded-xl p-5 border ${tier.popular ? 'border-emerald-400 scale-105 z-10' : colors.border} ${colors.bg} transition-all`}
                  >
                    <div className={`absolute -top-3 left-4 px-3 py-1 ${colors.badge} text-white text-xs font-semibold rounded-full`}>
                      {tier.tier}{tier.popular && ' — Most Popular'}
                    </div>
                    <div className="pt-3">
                      <h3 className="text-lg font-bold text-white mb-1">{tier.name}</h3>
                      <p className={`${colors.text} text-sm font-medium mb-3`}>{tier.mode}</p>
                      <p className="text-slate-300 text-sm">{tier.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Strategic Moats */}
            <div className="grid md:grid-cols-2 gap-4">
              {slide.moats.map((moat, idx) => {
                const moatColors = {
                  cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-300", icon: "text-cyan-400" },
                  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-300", icon: "text-emerald-400" }
                };
                const colors = moatColors[moat.color] || moatColors.cyan;
                return (
                  <div key={idx} className={`rounded-xl p-5 ${colors.bg} border ${colors.border}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <Shield className={`w-6 h-6 ${colors.icon}`} />
                      <div>
                        <h3 className="text-white font-semibold">{moat.category}</h3>
                        <p className={`${colors.text} text-xs`}>{moat.subtitle}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {moat.items.map((item, iidx) => (
                        <div key={iidx} className="space-y-1">
                          <p className="text-white font-medium text-sm">{item.title}</p>
                          <p className="text-slate-400 text-xs">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "cta":
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {slide.options.map((option, idx) => {
                const OptionIcon = option.icon;
                return (
                  <Link 
                    key={idx} 
                    to={option.link}
                    onClick={onClose}
                    className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-cyan-500/50 transition-all group text-center"
                  >
                    <div className="inline-flex p-3 bg-cyan-500/20 rounded-xl mb-4 group-hover:bg-cyan-500/30 transition-colors">
                      <OptionIcon className="w-8 h-8 text-cyan-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">{option.type}</h3>
                    <p className="text-slate-300 text-sm mb-4">{option.description}</p>
                    <span className="inline-flex items-center gap-2 text-cyan-300 font-medium text-sm group-hover:gap-3 transition-all">
                      {option.action}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-300">
              <a href={`mailto:${slide.contact.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                {slide.contact.email}
              </a>
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Toll Free {slide.contact.tollFree}
              </span>
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Direct {slide.contact.direct}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {slide.contact.website}
              </span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="dark-dialog max-w-6xl h-[90vh] border-slate-700 p-0 overflow-hidden [&>button]:text-white"
        data-dark-mode="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Play className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">AskIsaac Platform Demo</h3>
              <p className="text-slate-400 text-sm">Slide {currentSlide + 1} of {DEMO_SLIDES.length}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ShareButtons 
              title="AskIsaac - Healthcare Operations Platform"
              description="Connecting hospitals, contractors, and suppliers on one intelligent platform. See the demo!"
              url="https://sterilemate.com"
              variant="dark"
              size="sm"
            />
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white hover:bg-slate-800">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Video + Slide Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Digital Isaac Video Banner */}
          <div className="relative w-full h-48 md:h-56 bg-gradient-to-b from-slate-800/50 to-transparent overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                preload="auto"
                poster={DIGITAL_ISAAC}
                className="h-full w-auto object-contain opacity-90"
                style={{ 
                  filter: 'drop-shadow(0 0 40px rgba(96, 165, 250, 0.3))',
                  maxWidth: '100%'
                }}
              >
                <source src={DIGITAL_ISAAC_VIDEO} type="video/mp4" />
              </video>
            </div>
            {/* Gradient overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900 to-transparent" />
          </div>
          
          {/* Slide Content */}
          <div className="p-6 md:p-8 -mt-8 relative z-10">
            {renderSlide()}
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50">
          <div className="flex items-center justify-between">
            {/* Slide Indicators */}
            <div className="flex items-center gap-2">
              {DEMO_SLIDES.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => goToSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentSlide 
                      ? 'w-8 bg-cyan-500' 
                      : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                  title={s.title}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              {currentSlide < DEMO_SLIDES.length - 1 ? (
                <Button
                  size="sm"
                  onClick={nextSlide}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Link to="/login" onClick={onClose}>
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
